import { partialRight } from 'lodash';
import {
  get,
  toLower,
  camelCase,
  flow,
  defaultTo,
  mapValues,
  mapKeys,
  map,
  identity,
} from 'lodash/fp';

import typeOf from 'util/type-of';

/**
 * Converts object keys to lowercase, even when keys are snake or kebab case.
 */
const normalizeKeys = mapKeys(flow(camelCase, toLower));

/**
 * Returns a function capable of recursively transforming _any_ value using
 * a set of transformers provided as a type keyed object.
 *
 * @param  {Object} transformers Keys match type names (i.e. Array), values are
 *                               functions taking a value and returning the
 *                               transformed value. A special key "default"
 *                               allows specifying a fallback transformer.
 * @return {Function}            A function that will transform any value it
 *                               receives according to the transformers.
 */
export const transformWith = (transformers) => (val) => {
  const type = flow(typeOf, toLower)(val);
  const ts = normalizeKeys(transformers);
  const typeTransformer = flow(
    partialRight(get, ts),
    defaultTo(ts.default),
    defaultTo(identity),
  )(type);
  return typeTransformer(val);
};

/**
 * Will recursively trim String Object properties. Also handles Strings and
 * Arrays.
 */
export function trim(val) {
  return transformWith({
    array: map(trim),
    string: (str) => str.trim(),
    object: mapValues(trim),
  })(val);
}

export function camelCaseKeys(val) {
  return transformWith({
    object: mapKeys(camelCase),
  })(val);
}
